import * as React from 'react';
import { useEffect, useState } from 'react';
import './App.scss';
import { AppRoute } from './routers/app-router';
import { suppressDefaultPropsWarnings } from './utils/console-utils';
import { serviceWorkerManager } from './services/service-worker-manager';
import { ServiceWorkerWrapper } from './components/pwa';

// Suppress defaultProps warnings in development
suppressDefaultPropsWarnings();

const App = () => {
  const [swStatus, setSwStatus] = useState({
    isInitialized: false,
    error: null as string | null
  });

  useEffect(() => {
    // Initialize Service Worker based on environment
    const initServiceWorker = async () => {
      try {
        const isProduction = process.env.NODE_ENV === 'production';
        const isDevelopment = process.env.NODE_ENV === 'development';
        
        console.log(`🚀 Initializing Service Worker in ${process.env.NODE_ENV} mode...`);
        
        // Check if service worker manager is available
        if (!serviceWorkerManager || typeof serviceWorkerManager.initialize !== 'function') {
          console.error('❌ Service Worker manager not properly initialized');
          setSwStatus({ isInitialized: true, error: null }); // Don't block app
          return;
        }
        
        // Check if service worker is supported
        if (!('serviceWorker' in navigator)) {
          console.log('⚠️ Service Worker not supported in this browser');
          setSwStatus({ isInitialized: true, error: null });
          return;
        }

        // Check if we're in a WebView environment
        const userAgent = navigator.userAgent.toLowerCase();
        const isWebView = userAgent.includes('utilitytool') || 
                         userAgent.includes('wv') || 
                         (userAgent.includes('mobile') && userAgent.includes('safari') && !userAgent.includes('chrome')) ||
                         userAgent.includes('electron') ||
                         userAgent.includes('cordova') ||
                         userAgent.includes('capacitor') ||
                         userAgent.includes('react-native') ||
                         (window as any).ReactNativeWebView !== undefined;

        if (isWebView || isDevelopment) {
          console.log('📱 WebView environment detected, skipping service worker');
          setSwStatus({ isInitialized: true, error: null });
          return;
        }
        
        // Production mode: Strict initialization with proper error handling
        if (isProduction) {
          console.log('🏭 Production mode: Strict service worker initialization');
          
          try {
            // Wait for service worker to be ready with production timeout
            let success = await serviceWorkerManager.initialize();
            
            // If initial attempt fails, try retry mechanism for production
            if (!success && serviceWorkerManager?.retryInitialization) {
              console.log('🔄 Production: Initial attempt failed, trying retry mechanism...');
              try {
                success = await serviceWorkerManager.retryInitialization(3); // Try 3 more times in production
                console.log('✅ Production: Service Worker retry result:', success);
              } catch (retryError) {
                console.error('❌ Production: Service Worker retry failed:', retryError);
                success = false;
              }
            }
            
            if (success) {
              console.log('✅ Production: Service Worker initialized successfully');
              setSwStatus({ isInitialized: true, error: null });
              
              // Check for updates after successful initialization
              await checkForUpdates();
            } else {
              console.error('❌ Production: Service Worker initialization failed after all attempts');
              handleProductionError(new Error('Initialization failed after all attempts'), 'initialization');
            }
          } catch (initError) {
            handleProductionError(initError, 'initialization');
          }
        }
        // Development mode: Lenient initialization
        else if (isDevelopment) {
          console.log('🔧 Development mode: Lenient service worker initialization');
          
          try {
            const success = await serviceWorkerManager.initialize();
            
            if (success) {
              console.log('✅ Development: Service Worker initialized successfully');
              setSwStatus({ isInitialized: true, error: null });
              await checkForUpdates();
            } else {
              // In development, don't fail completely - allow app to continue
              console.warn('⚠️ Development: Service Worker not ready, but continuing...');
              setSwStatus({ isInitialized: true, error: null });
            }
          } catch (devError) {
            console.warn('⚠️ Development: Service Worker error, but continuing...', devError);
            setSwStatus({ isInitialized: true, error: null });
          }
        }
        // Other environments (staging, testing, etc.)
        else {
          console.log(`🌍 ${process.env.NODE_ENV} mode: Standard service worker initialization`);
          
          try {
            const success = await serviceWorkerManager.initialize();
            setSwStatus({ isInitialized: true, error: null });
            
            if (success) {
              console.log('✅ Service Worker initialized successfully');
              await checkForUpdates();
            } else {
              console.warn('⚠️ Service Worker not ready, but continuing...');
            }
          } catch (envError) {
            console.warn('⚠️ Service Worker error, but continuing...', envError);
            setSwStatus({ isInitialized: true, error: null });
          }
        }
        
      } catch (error) {
        console.error('❌ Critical Service Worker initialization error:', error);
        
        // In production, show error to user
        if (process.env.NODE_ENV === 'production') {
          handleProductionError(error, 'critical');
        } else {
          // In other environments, don't block the app
          setSwStatus({ isInitialized: true, error: null });
        }
      }
    };

    // Check for updates function
    const checkForUpdates = async () => {
      try {
        if (serviceWorkerManager?.checkVersion) {
          const versionInfo = await serviceWorkerManager.checkVersion();
          console.log('📱 Current app version:', versionInfo?.currentVersion || 'Unknown');
          
          if (versionInfo?.versionChanged) {
            console.log('🔄 Version changed detected');
          }
        }
      } catch (versionError) {
        console.warn('⚠️ Version check failed:', versionError);
      }
    };

    // Enhanced error handling for production
    const handleProductionError = (error: any, context: string) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`❌ Production ${context}:`, errorMessage);
      
      // Set appropriate error message for user
      let userMessage = 'Service Worker failed to start. Please refresh the page.';
      
      if (context.includes('network') || context.includes('connection')) {
        userMessage = 'Network connection issue. Please check your connection and refresh.';
      } else if (context.includes('timeout')) {
        userMessage = 'Service Worker is taking too long to start. Please refresh the page.';
      } else if (context.includes('permission')) {
        userMessage = 'Permission denied. Please allow notifications and refresh.';
      }
      
      setSwStatus({ 
        isInitialized: false, 
        error: userMessage 
      });
    };

    // Defer service worker initialization to avoid blocking initial render
    // Use requestIdleCallback if available for better performance
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        initServiceWorker();
      }, { timeout: 3000 });
    } else {
      // Fallback: delay initialization slightly to not block initial render
      const initDelay = process.env.NODE_ENV === 'production' ? 500 : 200;
      setTimeout(initServiceWorker, initDelay);
    }

    // Cleanup on unmount
    return () => {
      try {
        if (serviceWorkerManager && typeof serviceWorkerManager.destroy === 'function') {
          serviceWorkerManager.destroy();
          console.log('🧹 Service Worker manager cleaned up');
        }
      } catch (error) {
        console.error('❌ Error cleaning up Service Worker:', error);
      }
    };
  }, []);

  // Don't block the app while service worker initializes
  // Always render the main content, service worker will initialize in background
  
  // Optional: Show error banner if service worker failed (non-blocking)
  const showErrorBanner = swStatus.error && (
    <div className="service-worker-error-banner">
      <span>⚠️ Service Worker: {swStatus.error}</span>
      <button onClick={() => window.location.reload()}>Retry</button>
    </div>
  );

  return (
    <>
      {showErrorBanner}
      <ServiceWorkerWrapper 
        showStatus={false} // Hidden by default
        showNotifications={true} // Always show update notifications
        showDevPanel={false} // Hidden by default, only show with ?debug=true
      >
        <AppRoute />
      </ServiceWorkerWrapper>
    </>
  );
};

export default App;
